/* ==========================================================================
   Optimize Minerals — restyle the Skio plan picker

   Skio renders <skio-plan-picker> as a web component and puts all of its
   markup in a shadow root, so nothing in op-pdp.css can reach it. That is why
   the picker still looked like stock Skio while the blocks around it matched
   the design.

   The shadow root is open and supports adoptedStyleSheets, so the styles below
   are handed to it as a constructed sheet. adoptedStyleSheets rather than a
   <style> element on purpose: it is a property of the shadow root, not a child
   node, so it survives Skio re-rendering its contents when the variant or plan
   changes. A <style> tag would be wiped on the first re-render.

   Presentation only, with one exception - the picker keeps working exactly as
   it did. No rule targets the radio input, the selling-plan <select>, or the
   label wrapping them, and nothing re-shows an element Skio has chosen to hide.

   The exception is servingLine(), which adds one <span> per option holding the
   per-serving price. It is DOM rather than CSS because the figure comes from
   the theme's own data attributes, and because both options show their own
   figure at once - something content: attr() on a single element cannot do.

   THIS DEPENDS ON SKIO'S INTERNAL CLASS NAMES. If Skio ships a rename, these
   rules stop matching and the picker reverts to its stock appearance - it will
   not break the page or the subscription. The names relied on are:
     .skio-plan-picker  .skio-header  .group-container
     .group-container--subscription  .group-container--one-time
     .group-topline  .group-title  .group-title--time  .group-title--billed
     .savings.bubble  .skio-price  .skio-savings  .skio-savings__value
     .skio-savings__text  .skio-radio__container  .group-content
   ========================================================================== */

(function () {
  'use strict';

  /* PRESENTATION ONLY. Nothing here changes display on an element Skio has
     hidden, and nothing touches a form control - no rule targets the radio
     input, the selling-plan <select>, or the label that wraps them. The only
     display:none rules are on three pieces of duplicated text. That keeps the
     picker's behaviour, its plan selection and its cart wiring untouched. */
  /* The tick supplied for the subscription list. */
  var CHECK = 'data:image/svg+xml;charset=utf-8,%3Csvg%20width%3D%2213%22%20height%3D%2213%22%20viewBox%3D%220%200%2013%2013%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%20%3Crect%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2212%22%20height%3D%2212%22%20rx%3D%226%22%20stroke%3D%22%23ECEBE3%22%20stroke-opacity%3D%220.65%22%2F%3E%20%3Cpath%20d%3D%22M10.2137%205.0562C9.9679%204.74124%209.71439%204.40322%209.41479%204.03448C8.30857%205.34812%206.81057%207.17646%205.61984%208.3595C4.85932%207.65274%204.1372%206.8999%203.42277%206.13937C3.20767%206.33911%203.00025%206.53884%202.78516%206.73089C3.70701%207.76798%204.59044%208.83578%205.60448%209.78068C7.03335%208.0983%208.51599%206.47739%2010.2137%205.0562Z%22%20fill%3D%22%23ECEBE3%22%20fill-opacity%3D%220.65%22%2F%3E%20%3C%2Fsvg%3E';

  /* The chevron for the delivery-frequency dropdown, drawn in the panel's own
     light ink so it belongs to the control rather than the OS. */
  var CHEVRON_DARK = 'data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2214%22%20height%3D%228%22%20viewBox%3D%220%200%2014%208%22%20fill%3D%22none%22%3E%3Cpath%20d%3D%22M1%201L7%207L13%201%22%20stroke%3D%22%23174346%22%20stroke-opacity%3D%220.55%22%20stroke-width%3D%221.6%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E';

  var CHEVRON = 'data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2214%22%20height%3D%228%22%20viewBox%3D%220%200%2014%208%22%20fill%3D%22none%22%3E%3Cpath%20d%3D%22M1%201L7%207L13%201%22%20stroke%3D%22%23ECEBE3%22%20stroke-opacity%3D%220.75%22%20stroke-width%3D%221.6%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E';

  /* The same tick in the ink used on a light panel. Two data URIs rather than
     one recoloured at runtime: a background-image cannot inherit currentColor,
     so the only way to switch it with the panel's state is to switch the
     image. */
  /* The selected mark. A filled disc with the tick knocked out of it, as the
     reference draws it, in place of the plain dot Skio ships. Drawn as an
     image on the radio's own box rather than by reshaping Skio's <svg>:
     that svg is two <circle>s whose order and radii are Skio's business,
     and a rule that reached into them would break on any redraw. */
  var TICK_ON = 'data:image/svg+xml;charset=utf-8,%3Csvg%20width%3D%2220%22%20height%3D%2220%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Ccircle%20cx%3D%2210%22%20cy%3D%2210%22%20r%3D%2210%22%20fill%3D%22%23ECEBE3%22%2F%3E%3Cpath%20d%3D%22M5.9%2010.4l2.8%202.8%205.4-6%22%20stroke%3D%22%23174346%22%20stroke-width%3D%221.9%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E';

  var CHECK_DIM = 'data:image/svg+xml;charset=utf-8,%3Csvg%20width%3D%2213%22%20height%3D%2213%22%20viewBox%3D%220%200%2013%2013%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%20%3Crect%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2212%22%20height%3D%2212%22%20rx%3D%226%22%20stroke%3D%22%23174346%22%20stroke-opacity%3D%220.4%22%2F%3E%20%3Cpath%20d%3D%22M10.2137%205.0562C9.9679%204.74124%209.71439%204.40322%209.41479%204.03448C8.30857%205.34812%206.81057%207.17646%205.61984%208.3595C4.85932%207.65274%204.1372%206.8999%203.42277%206.13937C3.20767%206.33911%203.00025%206.53884%202.78516%206.73089C3.70701%207.76798%204.59044%208.83578%205.60448%209.78068C7.03335%208.0983%208.51599%206.47739%2010.2137%205.0562Z%22%20fill%3D%22%23174346%22%20fill-opacity%3D%220.4%22%2F%3E%20%3C%2Fsvg%3E';

  var CSS = [
    /* The component inherits the page's font only for properties that inherit,
       so the display face has to be named again inside the shadow root. */
    ':host { --om-display: "BlenderPro", "Arial Narrow", "Helvetica Neue", arial, sans-serif;',
    '        --om-body: "Suisse Intl", "Helvetica Neue", helvetica, arial, sans-serif;',
    '        --om-teal: #174346; --om-ink: #f3f2eb; --om-orange: #d66234;',
    '        --om-track: #ecebe3; --om-rule: rgba(23, 67, 70, 0.15);',
    /* The panel's own ink. #ECEBE3 in the spec, a shade off the page's
       #F3F2EB - which the orange chip is the one thing on the panel set in. */
    '        --om-sub-ink: #ecebe3; }',

    '.skio-plan-picker { display: flex; flex-direction: column; gap: 10px;',
    '  margin: 18px 0; padding: 0; border: 0; min-width: 0; }',

    /* "Purchase Options:" - the design has no heading over the options. */
    '.skio-header { display: none !important; }',

    /* Subscription first. Skio outputs one-time first in the DOM. Visual order
       only - it does not reorder the inputs for keyboard or assistive tech. */
    '.group-container--subscription { order: 1; }',
    '.group-container--one-time { order: 2; }',

    /* One radius across the buy box - the same --op-radius the Add to cart
       button uses. The column was running 8px on the panels, 6px on the
       tiles and the dropdown and 4px on the button, so six stacked controls
       had four different corners. Taken from the token rather than typed,
       so changing it once changes all of them. */
    '.group-container { position: relative; border-radius: var(--op-radius); margin: 0; }',

    /* ---- Subscription: the filled slate panel ---------------------------- */
    /* No padding here: Skio's own .group-label inside already carries 16px, and
       adding another 16 boxed the header row in 32px of space on every side. */
    /* ---- Selected / unselected ------------------------------------------- */
    /* The filled panel is whichever one is CHOSEN, not whichever one is the
       subscription. Picking one time turns that panel dark and hands the
       subscription back its light state, so the two always read as one control
       with one answer rather than a dark advert beside a plain row.

       Driven off :has(input:checked) - the input's own checkedness, which is
       the actual state - rather than Skio's .group-container--selected class,
       which is a name it could rename. Reading state is not changing it: no
       rule here touches the input, and both panels stay exactly as
       clickable as they were.

       The two inks are set as variables on the panel so every rule below can
       be written once and follow the state, instead of once per state. */
    '.group-container { --om-panel-ink: rgba(23, 67, 70, 0.55);',
    '  --om-panel-ink-soft: rgba(23, 67, 70, 0.5);',
    '  background: var(--om-track); border: 1px solid var(--om-rule); padding: 0; }',

    '.group-container:has(input:checked) { --om-panel-ink: var(--om-sub-ink);',
    '  --om-panel-ink-soft: rgba(236, 235, 227, 0.65);',
    '  background: var(--om-teal); border-color: transparent; }',

    /* Skio colours its radio itself and does it correctly on a light ground -
       that is what the one-time row has always looked like. Only the dark panel
       needs help, so only the dark panel is given a colour, and the unselected
       radio is left to Skio. Anything more would have painted the unselected
       radio as though it were on. */
    '.group-container:has(input:checked) .skio-radio__container,',
    '.group-container:has(input:checked) .skio-radio__container * { color: var(--om-sub-ink); }',

    /* A RADIO, not a tick. Skio's own graphic is already the right thing - an
       outer ring with a filled dot inside - and it is what the design draws, so
       it is simply left alone now. This used to hide that svg and paint a
       checkmark over the top, which said "done" where the control means
       "chosen": a tick reads as a completed step, and this is a choice between
       two rows that is still live. */
    '.group-container:has(input:checked) .skio-radio__container svg { visibility: visible; }',

    /* "Best Value" as the orange chip. Kept in the flow as its own right-aligned
       row above the title rather than positioned absolutely - absolute put it on
       top of the price, which sits in the same corner. */
    /* ------------------------------------------------------------------
       The subscribe panel as a GRID.

       Three rows - the choice, the schedule, what it costs - and three columns,
       with the radio and the chip pinned to the outer two. Skio's own markup
       nests these in a topline and a content block, so those two wrappers are
       dropped to display:contents and their children promoted into this grid.

       A grid rather than the absolute positioning this replaced. Absolute could
       put the chip and the price in roughly the right place but not ALIGN them:
       the chip has to sit on the title's centre line and the price on the
       centre line of the two bullets, and both of those move with their
       neighbour's height. align-items:center makes that the grid's job, so the
       alignment holds whatever the text does - a longer title, a third bullet,
       a plan name that wraps.
       ------------------------------------------------------------------ */
    '.group-container--subscription .group-label {',
    '  display: grid; grid-template-columns: auto 1fr auto;',
    '  align-items: center; column-gap: 12px; row-gap: 14px; }',
    '.group-container--subscription .group-topline,',
    '.group-container--subscription .group-content { display: contents; }',

    '.group-container--subscription .skio-radio__container { grid-row: 1; grid-column: 1; }',
    /* One line. The chip now occupies a real column beside it, so without
       this the title breaks after "SAVE" and the row grows to two lines for
       the sake of four characters. It fits at this size - the column is
       1fr and takes whatever the radio and chip leave. */
    '.group-container--subscription .group-title { grid-row: 1; grid-column: 2;',
    '  white-space: nowrap; }',
    /* position:static is what makes this a grid item at all. Skio pins the
       chip with position:absolute and top:-10px so it straddles the panel's
       top edge, and an absolutely positioned element ignores grid placement
       entirely - the row/column below were being silently discarded and the
       chip sat 11px above the title it is supposed to line up with. */
    '.group-container--subscription .skio-savings { position: static;',
    '  grid-row: 1; grid-column: 3;',
    '  justify-self: end; display: flex; margin: 0; padding: 0; background: none; }',
    '.group-container--subscription .selling-plan-dropdown { grid-row: 2; grid-column: 1 / -1; }',
    /* The wrapper has to measure the same as the list inside it, or centring
       the price against it centres against empty space: it was 65px tall
       around a 47px list, which sat the price 9px below the bullets it is
       supposed to line up with. Same reason the child's margin goes - a
       trailing margin is invisible but it still moves the centre. */
    /* The wrapper is taken out of the layout altogether rather than trimmed.
       Trimming its padding got the price within 3px of the bullets and stopped
       there, because the box still carried a line box of its own that nothing
       visible occupied. display:contents promotes the list itself into the
       grid, so the row IS the bullets and centring the price against it is
       centring against what a person actually sees. */
    '.group-container--subscription .group-content-additional { display: contents; }',
    /* The <ul> itself is the grid item, not either wrapper around it. The
       rich-text wrapper carried 6px above the list that no rule of ours owned,
       which offset the whole column by 3px once centred - close enough to look
       like a rounding error and wrong enough to see. Collapsing both wrappers
       leaves the list, which is the thing being aligned to. */
    /* The rich-text wrapper is the grid item, and everything inside it stays
       inside it. Promoting its children instead let a stray empty <p> that the
       metafield renders after the list auto-place itself into a FOURTH row -
       an invisible 6px band hanging off the bottom of the panel.

       font-size:0 is what the promotion was for. The wrapper measured 6px
       taller than the list it holds, from an anonymous line box around the
       whitespace between its tags; at zero font size that box has no height and
       the wrapper collapses onto the list. The list puts the size back for the
       text itself. */
    '.group-container--subscription .group-content-additional > *:not(.group-content-additional__variant-b) {',
    '  grid-row: 3; grid-column: 1 / 3;',
    '  margin: 0 !important; padding: 0 !important; font-size: 0; }',
    '.group-container--subscription .group-content-additional ul { margin: 0 !important; padding: 0 !important; }',
    /* And nothing inside the wrapper carries a margin of its own. The empty
       <p> has no height but it kept a 6px margin, which made the wrapper 6px
       taller than the list and pushed the price 3px off centre against it -
       invisible on its own, visible the moment it sits beside something. */
    '.group-container--subscription .group-content-additional > * > * { margin: 0 !important; }',
    /* !important because Skio's own padding-top on this wrapper wins on
       specificity, and it is 12px of dead space ABOVE the list - which the
       grid's row-gap already provides. Left in, it pushed the list down
       inside its own box so centring the price on the box put the price 9px
       above the bullets. */
    '.group-container--subscription .group-content-additional > * { margin: 0; }',
    '.group-container--subscription .skio-price { grid-row: 3; grid-column: 3; justify-self: end; }',
    /* Skio ships an empty label above the select; in a grid it would claim a cell. */
    '.group-container--subscription .selling-plan-dropdown-label { display: none; }',
    /* "Save $8" duplicates the chip beside it. */
    '.skio-savings__value { display: none !important; }',
    '.skio-savings__text { display: inline-flex; align-items: center; justify-content: center;',
    '  padding: 4px 8px; border-radius: 4px; background: var(--om-orange);',
    '  font-family: var(--om-display); font-weight: 800; font-size: 12px; line-height: 0.9;',
    '  letter-spacing: -0.01em; text-transform: uppercase; text-align: center;',
    '  color: var(--om-ink) !important;',
    '  text-box-trim: trim-both; text-box-edge: cap alphabetic; }',

    /* "Delivered every 30 days" duplicates the dropdown directly beneath it,
       which now states the schedule and lets it be changed. The design carries
       one of them, not both. */
    '.group-container--subscription .group-title--time { display: none; }',

    '.group-topline { display: flex; align-items: center; gap: 12px; }',
    /* 16px, measured off the design: the ring reads as a control at that size
       and stops competing with the title beside it. */
    '.skio-radio__container { flex: 0 0 auto; width: 16px; height: 16px; margin: 0; }',
    '.skio-radio__container svg { width: 100%; height: 100%; display: block; }',

    /* The title carries its label as a bare text node with the sub-lines as
       spans beside it, so the block is typed here and the spans re-typed below. */
    '.group-container--subscription .group-title { display: flex; flex-direction: column;',
    '  gap: 4px; font-family: var(--om-display); font-weight: 800; font-size: 18px;',
    '  line-height: 0.9; letter-spacing: -0.01em; text-transform: uppercase;',
    '  color: var(--om-panel-ink);',
    '  text-box-trim: trim-both; text-box-edge: cap alphabetic; }',

    /* Duplicates the chip two lines above it. */
    '.group-title .savings.bubble { display: none !important; }',

    /* Cadence and billing terms both stay visible. The comp does not draw them,
       but they are the terms of the subscription - "every 30 days" and "billed
       every 4 weeks" are not the same statement, and neither is repeated
       elsewhere on the page. Typed down so they read under the title. */
    '.group-title--time, .group-title--billed { font-family: var(--om-body); font-weight: 400;',
    '  font-size: 12px; line-height: 1.3; letter-spacing: -0.01em; text-transform: none;',
    '  color: var(--om-panel-ink-soft) !important; }',

    /* The per-serving figure, directly under the option's name. Sized between
       the title and the cadence line: it is the number being compared between
       the two options, so it should not read as fine print. */
    '.op-serving-line { display: block; font-family: var(--om-body); font-weight: 400;',
    '  font-size: 14px; line-height: 1.3; letter-spacing: -0.01em; text-transform: none;',
    '  margin-top: 2px; color: var(--om-panel-ink-soft) !important; }',

    /* Price to the right of the row: current above, struck original below.
       column-reverse because Skio puts the <s> first in the DOM, which stacked
       the crossed-out price over the one being charged. Visual order only. */
    /* The price sits in the bottom row beside the bullets - the top row is the
       choice and its badge, the bottom row is what that choice costs. */
    '.group-container--subscription .skio-price { text-align: right;',
    '  display: flex; flex-direction: column-reverse; align-items: flex-end; gap: 3px;',
    '  font-family: var(--om-display); font-weight: 800; font-size: 18px; line-height: 0.9;',
    '  letter-spacing: -0.01em; text-transform: uppercase; color: var(--om-panel-ink); }',
    /* The struck original is not specified on its own. Same face and metrics as
       the price it sits under, a step down and dimmed, so the pair reads as one
       block with the charge on top. */
    /* font-family named again: Skio sets its own face on the <s> itself, which
       out-specifies anything inherited, so the struck price came through in
       Gotham while the price above it was BlenderPro. */
    /* 15px, and a 6px step above it. Measured off the comp: the struck figure
       runs about 0.85 of the price it qualifies, not 0.7, and the pair sits on
       a 21px pitch rather than the 17px the stock sizes give. Too small and too
       close, the two read as one smudged number instead of a price and the
       price it replaced. */
    '.group-container--subscription .skio-price { gap: 6px; }',
    '.group-container--subscription .skio-price s { font-family: var(--om-display);',
    '  font-size: 15px; font-weight: 500; line-height: 0.9; letter-spacing: -0.01em;',
    '  color: var(--om-panel-ink-soft) !important; }',

    /* Type only - no display, so whichever of its two lists Skio has hidden
       stays hidden. Setting display here showed both and printed the benefits
       twice. */
    /* Skio draws a 1px rule above the bullets as a border-top on
       .group-content-additional. It is not in the design - the panel already
       separates the header from the list with space - so it is removed below,
       and this margin is zeroed so the gap above the list is set in one place
       (that block's own padding) rather than accumulated from three. */
    '.group-container--subscription .group-content { margin: 0; }',

    /* The supplied tick, in place of Skio's own marker. Inlined as a data URI
       rather than an asset URL: this stylesheet is a string inside a JS file,
       so it never passes through Liquid and has no asset_url to call. Drawn as
       a background on the item, which then carries the space for it. */
    '.group-container--subscription .group-content li {',
    '  list-style: none;',
    '  padding-left: 21px;',
    '  background-image: url("' + CHECK + '");',
    '  background-repeat: no-repeat;',
    '  background-position: 0 0.2em;',
    '  background-size: 13px 13px; }',
    /* border-top: 0 - Skio's `border-top: solid 1px #000000`, plus the
       #959D94 the variant-b rule recolours it to. Not tied to either group
       container, so the rule is gone from the one-time panel as well.
       margin-top: 0 goes with it, and carries !important for the same reason:
       that 10px existed only to hold the rule off the text above, and with the
       rule gone it is a gap nothing is holding. The padding below sets the one
       real gap.

       Hung off .skio-plan-picker rather than written bare so it out-specifies
       Skio's own (0,1,0) rule on class count alone, without depending on
       adopted sheets cascading after the shadow root's <style> - which is the
       order in every engine here, but is not what the fallback path uses. */
    '.skio-plan-picker .group-content-additional { border-top: 0 !important; margin-top: 0 !important; }',
    '.group-container--subscription .group-content-additional { padding-top: 12px; }',

    /* The tick is a list marker, and markers sit OUTSIDE the item box, so with
       no padding on the list they hung into the panel's own padding - left of
       the radio they should line up under. Positioning them inside puts the
       tick at the item's content edge, which is the same edge the radio starts
       from. list-style-position, not display: the list's layout stays Skio's. */
    '.group-container--subscription .group-content ul { list-style-position: inside; }',
    /* margin on the item, not gap on the list - the list's display is left to
       Skio so that whichever of its two lists it hides stays hidden. */
    /* Cap-height trim is specified here too, but the tick is a background on this
       same box - trimming it would move the tick off the cap line it is set
       against. Left untrimmed so the marker stays where it is drawn. */
    /* The two columns keep their OWN rhythms, and the design is explicit about
       it: measured off the comp the bullets step 26px and the prices 21px, the
       price column being the tighter of the two. What squares the row up is the
       two BLOCKS being centred on each other, which the grid does - not the
       individual lines meeting, which they do not do in the design either.

       Forcing both onto one pitch was tried and is wrong: it sets each price
       exactly beside its bullet, which reads as a table of pairs rather than a
       price attached to the offer as a whole. */
    '.group-container--subscription .group-content li { font-family: var(--om-body);',
    '  font-weight: 400; font-size: 15px; line-height: 1.35; letter-spacing: -0.01em;',
    '  margin-bottom: 6px; color: var(--om-panel-ink-soft) !important; }',
    '.group-container--subscription .group-content li:last-child { margin-bottom: 0; }',

    /* ---- One-time -------------------------------------------------------- */
    /* Shown. Skio hides this because the theme used to render its own one-time
       control under Add to cart - but that one added straight to the cart
       rather than selecting anything. That control is gone now, so this is the
       real second option: Skio already wires it to the selling_plan input and
       to the button's price, and it is the only one that does. */
    /* No padding here either: .group-label inside already carries 16px, and
       adding more on top made this row twice the height of the flavour row
       above it. */
    '.group-container--one-time { display: block !important; padding: 0; }',
    /* Its content block is an empty list on this store - it would only add a
       gap under the row. */
    '.group-container--one-time .group-content { display: none; }',
    /* Centred, not top-aligned. The topline aligns to flex-start so the
       subscription's two-line title and price hang off the radio's top edge -
       right there, wrong here, where each side is a single cap-trimmed line
       about 10px tall against a 20px radio, leaving the label sitting ~5px
       above the radio it labels. */
    '.group-container--one-time .group-topline { align-items: center; }',
    '.group-container--one-time .group-title { font-family: var(--om-display); font-weight: 800;',
    '  font-size: 18px; line-height: 0.9; letter-spacing: -0.01em; text-transform: uppercase;',
    '  color: var(--om-panel-ink);',
    '  text-box-trim: trim-both; text-box-edge: cap alphabetic; }',
    '.group-container--one-time .skio-price { margin-left: auto; font-family: var(--om-display);',
    '  font-weight: 800; font-size: 18px; line-height: 0.9; letter-spacing: -0.01em;',
    '  text-transform: uppercase; color: var(--om-panel-ink); }',

    /* ---- The subscription's terms, when it is not the one chosen ---------- */
    /* Struck through, as the reference does it: the benefits stay legible but
       are plainly not what is being bought. Only the list is struck - the price
       beside it is still the price of the plan, and scoring it out would read
       as though the subscription had been withdrawn rather than merely not
       selected. */
    '.group-container--subscription:not(:has(input:checked)) .group-content li {',
    '  text-decoration: line-through; }',

    /* The tick swaps with the ground it is drawn on. */
    '.group-container--subscription:not(:has(input:checked)) .group-content li {',
    '  background-image: url("' + CHECK_DIM + '"); }',

    /* ------------------------------------------------------------------
       The delivery frequency dropdown.

       This is the ONE place the file re-shows something Skio hides, and it is
       deliberate. Skio adds .selling-plan-dropdown--one when a product has a
       single frequency and hides the control, which is reasonable on its own -
       but the design puts the dropdown in the subscribe panel as a standing
       part of the layout, and a box that appears only for some products reads
       as a glitch on the ones without it. It also tells a shopper that the
       schedule is theirs to choose before they have committed to anything.

       It stays a real <select>: no rule here touches its value, its options or
       its change handler, so Skio still owns which plan is selected. The
       options are Skio's own plan names - to offer 60 and 90 day delivery,
       those plans have to exist in Skio; this control will list them the
       moment they do, with no further change here.
       ------------------------------------------------------------------ */
    '.selling-plan-dropdown, .selling-plan-dropdown--one {',
    /* !important because Skio hides the single-frequency case from its own
       adopted sheet, and which sheet wins depends on the order they were
       adopted - that order is Skio's to change on any re-render. */
    '  display: block !important; width: 100%; margin: 0;',
    /* pointer-events too, not just display. Skio switches BOTH off for the
       single-frequency case, so overriding only the first left a control
       that looked interactive and swallowed every click. */
    '  pointer-events: auto !important;',
    '  height: 40px;',
    /* Padding that fits INSIDE the 40px, or the descenders clip: 40 less two
       13s leaves 14px of content box for a 15px face. */
    '  padding: 0 42px 0 16px; border: 0; border-radius: var(--op-radius);',
    '  background-color: rgba(236, 235, 227, 0.10);',
    '  background-image: url("' + CHEVRON + '");',
    '  background-repeat: no-repeat; background-position: right 16px center;',
    '  background-size: 13px auto;',
    '  -webkit-appearance: none; -moz-appearance: none; appearance: none;',
    '  font-family: var(--op-font-body); font-size: 15px; line-height: 40px;',
    '  letter-spacing: -0.01em; cursor: pointer; box-shadow: none; }',

    /* The panel flips from dark to light when the other option is chosen, so
       the control inside it has to flip too. Given one set of colours it was
       warm white on warm white the moment subscription was deselected: an
       invisible box leaving a blank band in the middle of the panel, which is
       worse than not drawing it at all. */
    '.group-container--subscription:has(input:checked) .selling-plan-dropdown {',
    '  background-color: rgba(236, 235, 227, 0.10);',
    '  color: rgba(236, 235, 227, 0.75);',
    '  background-image: url("' + CHEVRON + '"); }',

    '.group-container--subscription:not(:has(input:checked)) .selling-plan-dropdown {',
    '  background-color: rgba(23, 67, 70, 0.06);',
    '  color: rgba(23, 67, 70, 0.55);',
    '  background-image: url("' + CHEVRON_DARK + '"); }',

    /* The native menu draws in the OS palette, so its options need ink of
       their own - light-on-light is unreadable on macOS otherwise. */
    '.selling-plan-dropdown option { color: var(--op-slate-teal); background: var(--op-off-white); }',

    '.selling-plan-dropdown:focus-visible {',
    '  outline: 2px solid var(--op-deep-orange); outline-offset: 2px; }',

    /* The one-time row carries no price in the design, and it is right not to.
       The price it showed was the per-canister figure, which is already stated
       under the title and again on the button - three copies of one number,
       and the only one here that could be mistaken for the row's own total.
       The row is a CHOICE between two ways to buy; the price of the choice you
       land on is directly below it. */
    '.group-container--one-time .skio-price { display: none; }'
  ].join('\n');

  var sheet = null;

  function getSheet() {
    if (sheet) return sheet;
    try {
      sheet = new CSSStyleSheet();
      sheet.replaceSync(CSS);
    } catch (e) {
      sheet = false; // constructed sheets unavailable - fall back below
    }
    return sheet;
  }

  function apply(el) {
    var root = el.shadowRoot;
    if (!root || el.dataset.omStyled === '1') return;

    var s = getSheet();
    if (s && root.adoptedStyleSheets && root.adoptedStyleSheets.indexOf(s) === -1) {
      // Concat rather than push: adoptedStyleSheets is frozen in some engines.
      root.adoptedStyleSheets = root.adoptedStyleSheets.concat([s]);
      el.dataset.omStyled = '1';
      return;
    }

    // Fallback for engines without constructed stylesheets. A <style> node can
    // be dropped by a re-render, so the observer below puts it back.
    if (!root.querySelector('style[data-om-skio]')) {
      var tag = document.createElement('style');
      tag.setAttribute('data-om-skio', '');
      tag.textContent = CSS;
      root.appendChild(tag);
    }
  }

  /* ------------------------------------------------------------------
     Per-serving price under each option

     The design puts "$0.50 per serving" under SUBSCRIBE & SAVE and the
     one-time equivalent under ONE TIME PURCHASE, so the two can be compared
     without doing the arithmetic. Both figures are shown at once, which is why
     this cannot reuse .js-price-serving - that element holds one figure, for
     whichever mode is currently chosen.

     The numbers are the theme's own, not a second calculation:

       a pack is selected -> data-pack-serving-onetime / -subscription on the
                             checked .op-pack__radio, so the figure follows the
                             1/2/4 month choice
       no pack selector   -> data-serving-onetime / -subscription on
                             .js-price-serving, computed in main-product.liquid

     Nothing is invented when those are absent: a product with no servings
     count renders no line at all rather than a guess.

     This is the one part of this file that writes DOM rather than CSS. It adds
     a single <span> per group and never touches a radio, a <select> or a
     label, so the picker's behaviour and its cart wiring are untouched. Skio
     re-renders its shadow content on variant and plan changes, which wipes the
     span - scan() below runs again on those mutations and puts it back.
     ------------------------------------------------------------------ */
  function servingFigures() {
    var pack = document.querySelector('.op-pack__radio:checked');
    if (pack && pack.getAttribute('data-pack-serving-onetime')) {
      return {
        one: pack.getAttribute('data-pack-serving-onetime'),
        sub: pack.getAttribute('data-pack-serving-subscription')
      };
    }
    var el = document.querySelector('.js-price-serving');
    if (el && el.getAttribute('data-serving-onetime')) {
      return {
        one: el.getAttribute('data-serving-onetime'),
        sub: el.getAttribute('data-serving-subscription')
      };
    }
    return null;
  }

  function servingLine(root) {
    var figures = servingFigures();
    if (!figures) return;

    var groups = root.querySelectorAll('.group-container');
    for (var i = 0; i < groups.length; i++) {
      var group = groups[i];
      var title = group.querySelector('.group-title');
      if (!title) continue;

      /* One time is the only group that is not a subscription, and Skio marks
         it on the container. Anything else - subscribe, prepaid - is priced
         from the plan. */
      var isOneTime = group.classList.contains('group-container--one-time');
      var text = (isOneTime ? figures.one : figures.sub);
      if (!text) continue;
      text = text + ' per serving';

      var line = title.querySelector('.op-serving-line');
      if (!line) {
        line = document.createElement('span');
        line.className = 'op-serving-line';
        /* Straight after the group's name, before the delivery frequency. */
        var time = title.querySelector('.group-title--time');
        if (time) title.insertBefore(line, time);
        else title.appendChild(line);
      }
      if (line.textContent !== text) line.textContent = text;
    }
  }

  function scan() {
    var pickers = document.querySelectorAll('skio-plan-picker');
    for (var i = 0; i < pickers.length; i++) {
      apply(pickers[i]);
      if (pickers[i].shadowRoot) servingLine(pickers[i].shadowRoot);
    }
    return pickers.length;
  }

  /* The pack choice changes the per-serving figure but not Skio's own markup,
     so the mutation observer below never sees it. */
  document.addEventListener('change', function (e) {
    if (e.target && e.target.classList && e.target.classList.contains('op-pack__radio')) scan();
  });

  // The element is upgraded by Skio's own script, which may land after this
  // one, so watch for it as well as scanning now.
  scan();
  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('skio-plan-picker').then(scan).catch(function () {});
  }
  document.addEventListener('DOMContentLoaded', scan);

  // Catches the picker being added late, and re-attaches the fallback <style>
  // if Skio replaces its shadow content. Scoped to the buy column where the
  // block lives rather than the whole document.
  var host = document.querySelector('.product__info-container') || document.body;
  if (window.MutationObserver) {
    new MutationObserver(scan).observe(host, { childList: true, subtree: true });
  }
})();